import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { BlockPreviewSettingsService } from './block-preview-settings.service';
import { BlockPreviewSource } from './block-preview-source';
import { BlockPreviewState, idleState } from './block-preview-state';
import { BlockPreviewController } from './block-preview.controller';

/**
 * The preview (#263) as a host mounts it: give it a source, and it renders what the source holds through its own
 * controller and draws the panel. `layout` is where it sits: 'column' (a sticky column beside a form, in the Pages and
 * Region editors) or 'stage' (filling the Suburbs tool's stage). README.md in this folder has the states.
 */
@Component({
  selector: 'app-block-preview',
  template: `<app-block-preview-panel
    [state]="state"
    [srcdoc]="controller.srcdoc(state.html)"
    [closable]="closable"
    (refresh)="controller.refresh()"
    (closed)="closed.emit()"
  ></app-block-preview-panel>`,
  styles: [
    `
      :host {
        display: block;
      }
      :host(.column) {
        flex: 0 0 auto;
        width: min(46%, 44rem);
        position: sticky;
        top: 0;
        height: calc(100vh - 14rem);
        min-height: 24rem;
      }
      :host(.stage) {
        height: 100%;
      }
    `,
  ],
  providers: [BlockPreviewController],
})
export class BlockPreviewComponent implements AfterViewInit, OnDestroy {
  @Input() set source(source: BlockPreviewSource | null) {
    this.controller.bind(source);
  }
  @Input() layout: 'column' | 'stage' = 'column';
  @Input() closable = true;
  @Output() closed = new EventEmitter<void>();

  state: BlockPreviewState = idleState();

  private sub: Subscription;
  private viewReady = false;

  @HostBinding('class.column') get isColumn(): boolean {
    return this.layout === 'column';
  }
  @HostBinding('class.stage') get isStage(): boolean {
    return this.layout === 'stage';
  }

  constructor(
    public controller: BlockPreviewController,
    private settings: BlockPreviewSettingsService,
    private cdr: ChangeDetectorRef
  ) {
    // Answers can land outside Angular's zone (the Suburbs tool), so each state redraws this view itself.
    this.sub = controller.state$.subscribe((state) => {
      this.state = state;
      if (state.phase === 'off') settings.notConfigured();
      if (this.viewReady) this.cdr.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
  }

  ngOnDestroy(): void {
    this.viewReady = false;
    this.sub.unsubscribe();
  }
}
